import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ImageField from "@/components/admin/ImageField";

export default function CrudManager({ title, subtitle, listEndpoint, adminEndpoint, fields, columns }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [confirmId, setConfirmId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = () => api.get(listEndpoint).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openNew = () => {
    const init = {};
    fields.forEach((f) => (init[f.name] = f.default ?? ""));
    setForm(init);
    setEditing(null);
    setOpen(true);
  };

  const openEdit = (item) => {
    setForm({ ...item });
    setEditing(item.id);
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {};
    fields.forEach((f) => {
      let v = form[f.name] ?? "";
      if (f.type === "number") v = Number(v) || 0;
      payload[f.name] = v;
    });
    try {
      if (editing) await api.put(`${adminEndpoint}/${editing}`, payload);
      else await api.post(adminEndpoint, payload);
      toast.success(editing ? "Bijgewerkt" : "Toegevoegd");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    setDeleting(true);
    try {
      await api.delete(`${adminEndpoint}/${id}`);
      toast.success("Verwijderd");
      setConfirmId(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setDeleting(false);
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div data-testid={`admin-${title.toLowerCase()}-page`}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-serif text-4xl text-[#0A3087]">{title}</h1>
          {subtitle && <p className="text-[#0B0B0C]/60 text-sm mt-1">{subtitle}</p>}
        </div>
        <button onClick={openNew} data-testid={`admin-add-${title.toLowerCase()}`} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md text-white text-sm font-600 transition-transform hover:-translate-y-0.5" style={{ background: "var(--elsa-blue)" }}>
          <Plus size={16} /> Nieuw
        </button>
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#F4F5F7] text-left text-[#0B0B0C]/60">
              {columns.map((c) => <th key={c.key} className="px-5 py-3 font-500">{c.label}</th>)}
              <th className="px-5 py-3 text-right font-500">Acties</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id} className="border-t border-[#E2E8F0] hover:bg-[#F9F9F6]" data-testid={`admin-row-${it.id}`}>
                {columns.map((c) => (
                  <td key={c.key} className="px-5 py-4 text-[#0B0B0C]">{c.render ? c.render(it) : it[c.key]}</td>
                ))}
                <td className="px-5 py-4">
                  <div className="flex justify-end gap-2">
                    <button onClick={() => openEdit(it)} data-testid={`admin-edit-${it.id}`} className="h-8 w-8 grid place-items-center rounded-md border border-[#E2E8F0] hover:border-[#0A3087] hover:text-[#0A3087]"><Pencil size={14} /></button>
                    <button onClick={() => setConfirmId(it.id)} data-testid={`admin-delete-${it.id}`} className="h-8 w-8 grid place-items-center rounded-md border border-[#E2E8F0] hover:border-red-500 hover:text-red-500"><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={columns.length + 1} className="px-5 py-10 text-center text-[#0B0B0C]/40">Nog geen items.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-2xl text-[#0A3087]">{editing ? "Bewerken" : "Nieuw item"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4" data-testid="admin-crud-form">
            {fields.map((f) => (
              <div key={f.name}>
                <Label htmlFor={f.name}>{f.label}</Label>
                {f.type === "textarea" ? (
                  <Textarea id={f.name} value={form[f.name] ?? ""} onChange={set(f.name)} className="mt-1.5" rows={3} />
                ) : f.type === "image" ? (
                  <ImageField value={form[f.name] ?? ""} onChange={(v) => setForm((prev) => ({ ...prev, [f.name]: v }))} testid={`admin-image-${f.name}`} />
                ) : f.type === "select" ? (
                  <select id={f.name} value={form[f.name] ?? ""} onChange={set(f.name)} className="mt-1.5 h-10 w-full rounded-md border border-input bg-background px-3 text-sm">
                    {f.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                ) : f.type === "date" ? (
                  <div className="mt-1.5 flex items-center gap-2">
                    <Input id={f.name} type="date" value={form[f.name] ?? ""} onChange={set(f.name)} className="flex-1" />
                    {form[f.name] ? (
                      <button type="button" onClick={() => setForm((prev) => ({ ...prev, [f.name]: "" }))} data-testid={`admin-clear-${f.name}`} className="shrink-0 px-3 h-10 rounded-md border border-[#E2E8F0] text-sm text-[#0B0B0C]/70 hover:border-[#0A3087] hover:text-[#0A3087] transition-colors">
                        Wissen
                      </button>
                    ) : (
                      <span className="shrink-0 text-xs text-[#0B0B0C]/50 px-1">Coming soon</span>
                    )}
                  </div>
                ) : (
                  <Input id={f.name} type={f.type === "number" ? "number" : "text"} value={form[f.name] ?? ""} onChange={set(f.name)} className="mt-1.5" required={f.required} />
                )}
              </div>
            ))}
            <button type="submit" disabled={saving} data-testid="admin-crud-save" className="w-full py-3 rounded-md text-white font-600 text-sm disabled:opacity-60" style={{ background: "var(--elsa-blue)" }}>
              {saving ? "Opslaan..." : "Opslaan"}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmId} onOpenChange={(v) => !v && setConfirmId(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-serif text-2xl text-[#0A3087]">Verwijderen?</DialogTitle></DialogHeader>
          <p className="text-sm text-[#0B0B0C]/70">Weet je zeker dat je dit item wil verwijderen? Deze actie kan niet ongedaan gemaakt worden.</p>
          <div className="flex justify-end gap-3 mt-2">
            <button onClick={() => setConfirmId(null)} data-testid="admin-delete-cancel" className="px-4 py-2.5 rounded-md border border-[#E2E8F0] text-sm font-500 hover:bg-[#F4F5F7]">Annuleren</button>
            <button onClick={() => remove(confirmId)} disabled={deleting} data-testid="admin-delete-confirm" className="px-4 py-2.5 rounded-md bg-red-600 text-white text-sm font-600 hover:bg-red-700 disabled:opacity-60">
              {deleting ? "Verwijderen..." : "Verwijderen"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
